// packages/chatbot/src/contexts/ChatbotOverridesContext.tsx

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ChatbotProps } from '../types';

// Be permissive: allow extra keys beyond ChatbotProps so host apps can pass
// page-specific metadata (e.g., widgetTemplateId, allowedActions) without TS friction.
export type ChatbotOverrides = Partial<ChatbotProps> & Record<string, any>;

export type ChatbotOverridesContextValue = {
  overrides: ChatbotOverrides;
  setOverrides: (
    next: ChatbotOverrides | ((prev: ChatbotOverrides) => ChatbotOverrides)
  ) => void;
};

const ChatbotOverridesContext = createContext<ChatbotOverridesContextValue | null>(null);

type ProviderProps =
  | {
      value?: ChatbotOverridesContextValue;
      children: React.ReactNode;
    }
  | {
      // Convenience: allow passing initial overrides directly
      overrides?: ChatbotOverrides;
      children: React.ReactNode;
    };

/**
 * Wrap a page/route with this provider to supply page-level overrides.
 * Supports two shapes:
 * - <ChatbotOverridesProvider overrides={{ perTurnContext, perTurnPrompt, ... }}>{children}</ChatbotOverridesProvider>
 * - <ChatbotOverridesProvider value={{ overrides, setOverrides }}>{children}</ChatbotOverridesProvider>
 */
export function ChatbotOverridesProvider(props: ProviderProps) {
  // If caller provided a full value (overrides + setOverrides), forward it
  if ('value' in props && props.value) {
    return (
      <ChatbotOverridesContext.Provider value={props.value}>
        {props.children}
      </ChatbotOverridesContext.Provider>
    );
  }

  const [overrides, set] = useState<ChatbotOverrides>(() => {
    if ('overrides' in props && props.overrides) return props.overrides;
    return {};
  });

  const setOverrides = useCallback(
    (next: ChatbotOverrides | ((prev: ChatbotOverrides) => ChatbotOverrides)) => {
      if (typeof next === 'function') {
        set(prev => (next as any)(prev) || {});
      } else {
        set(next || {});
      }
    },
    []
  );

  const value = useMemo<ChatbotOverridesContextValue>(
    () => ({ overrides, setOverrides }),
    [overrides, setOverrides]
  );

  return (
    <ChatbotOverridesContext.Provider value={value}>
      {props.children}
    </ChatbotOverridesContext.Provider>
  );
}

/**
 * Returns the current page-level overrides API:
 * - overrides: current overrides object
 * - setOverrides: replace or update overrides
 *
 * Throws if used outside a provider to surface integration mistakes early.
 */
export function useChatbotOverrides(): ChatbotOverridesContextValue {
  const ctx = useContext(ChatbotOverridesContext);
  if (!ctx) {
    throw new Error('useChatbotOverrides must be used within ChatbotOverridesProvider');
  }
  return ctx;
}
